import React, { useEffect, useState } from "react";
import { useFormik } from "formik";
import * as yup from "yup";
import Button from "@material-ui/core/Button";
import Checkbox from "@material-ui/core/Checkbox";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import TextField from "@material-ui/core/TextField";
import IconButton from "@material-ui/core/IconButton";
import InputAdornment from "@material-ui/core/InputAdornment";
import LockOutlined from "@material-ui/icons/LockOutlined";
import PersonOutline from "@material-ui/icons/PersonOutline";
import Visibility from "@material-ui/icons/Visibility";
import VisibilityOff from "@material-ui/icons/VisibilityOff";
import AuthApi from "../../../Services/Authapi";
import swal from "sweetalert";
import ls from "local-storage";
import { Link } from "react-router-dom";
import {
  persistStoredRole,
  persistStoredUser,
  syncRoleToggleEligibility,
  recordLoginSession,
} from "../../../../utils/authSession";

const validationSchema = yup.object({
  email: yup
    .string("Enter your email")
    .email("Enter a valid email")
    .required("Email is required"),
  password: yup.string("Enter your password").required("Password is required"),
});

const LoginForm = (props) => {
  const [showPassword, setShowPassword] = useState(false);
  const [remember, setRemember] = useState(true);

  const getPermissionNames = (roles = []) =>
    Array.isArray(roles)
      ? roles.flatMap((role) =>
          Array.isArray(role?.permissions)
            ? role.permissions
                .map((permission) => permission?.name)
                .filter((permissionName) => typeof permissionName === "string" && permissionName.trim() !== "")
            : []
        )
      : [];

  const formik = useFormik({
    initialValues: {
      email: "",
      password: "",
    },
    validationSchema: validationSchema,
    onSubmit: async (values) => {
      const checkLogin = await AuthApi.login(values);

      if (checkLogin && checkLogin.access_token) {
        ls.set("authToken", checkLogin.access_token);
        recordLoginSession();
        const primaryRole = persistStoredRole(checkLogin.user.roles[0].name);
        persistStoredUser(checkLogin.user);
        syncRoleToggleEligibility({
          user: checkLogin.user,
          role: primaryRole,
        });
        ls.set(
          "userAccess",
          Array.isArray(checkLogin.data?.userAccess) && checkLogin.data.userAccess.length > 0
            ? checkLogin.data.userAccess
            : getPermissionNames(checkLogin.user?.roles)
        );
        ls.set("isActive", false);
        props.setAutUser({
          authUser: checkLogin.data,
          authToken: checkLogin.access_token,
          roles: primaryRole,
          user: checkLogin.user,
        });

        const userTheme = checkLogin.user?.theme || "light";
        if (typeof window !== "undefined") {
          window.localStorage.setItem("theme", userTheme);
          const isDark = userTheme === "dark";
          if (typeof document !== "undefined") {
            document.body.classList.toggle("dark-theme", isDark);
            document.documentElement.setAttribute("data-theme", userTheme);
            document.documentElement.style.setProperty("color-scheme", userTheme);
          }
        }

        try {
          window.localStorage.setItem("authToken", checkLogin.access_token);
        } catch (e) {}

        window.location.href = "/dashboard";
      } else {
        const errorMessage = typeof checkLogin === "string"
          ? checkLogin
          : (checkLogin && checkLogin.message) || "Login failed";
        const errorRef = checkLogin && checkLogin.error_ref ? `\nError Ref: ${checkLogin.error_ref}` : "";
        const statusCode =
          checkLogin && checkLogin.status_code && Number(checkLogin.status_code) !== 401
            ? `\nStatus Code: ${checkLogin.status_code}`
            : "";

        swal("Login Failed", `${errorMessage}${statusCode}${errorRef}`, "error");
      }
    },
  });

  useEffect(() => {
    if (props.authToken === false) {
      props.history.push("/login");
    }
  }, [props.authToken, props.history]);

  return (
    <div className="hrms-login-form-wrap">
      <form className="hrms-login-form" onSubmit={formik.handleSubmit}>
        <TextField
          fullWidth
          id="email"
          name="email"
          placeholder="Username"
          variant="outlined"
          value={formik.values.email}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          error={formik.touched.email && Boolean(formik.errors.email)}
          helperText={formik.touched.email && formik.errors.email}
          className="hrms-login-form__field"
          autoComplete="email"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <PersonOutline />
              </InputAdornment>
            ),
          }}
        />

        <TextField
          fullWidth
          id="password"
          name="password"
          variant="outlined"
          placeholder="Password"
          type={showPassword ? "text" : "password"}
          value={formik.values.password}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          error={formik.touched.password && Boolean(formik.errors.password)}
          helperText={formik.touched.password && formik.errors.password}
          className="hrms-login-form__field"
          autoComplete="current-password"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <LockOutlined />
              </InputAdornment>
            ),
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  aria-label="toggle password visibility"
                  className="hrms-login-form__visibility"
                  onClick={() => setShowPassword(!showPassword)}
                  edge="end"
                >
                  {showPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />

        <div className="hrms-login-form__meta">
          <FormControlLabel
            className="hrms-login-form__remember"
            control={
              <Checkbox
                checked={remember}
                onChange={(event) => setRemember(event.target.checked)}
                color="primary"
              />
            }
            label="Remember"
          />
          <Link className="hrms-login-form__forgot" to="/forgotpassword">
            Forgot password?
          </Link>
        </div>

        <Button
          className="hrms-login-form__submit"
          color="primary"
          variant="contained"
          type="submit"
        >
          LOGIN
        </Button>
      </form>
    </div>
  );
};

export default LoginForm;
